import { useCallback } from 'react';

import { usePico } from '../pico';
import {
  PAYWALL_FEATURE_STORAGE_KEY,
  PAYWALL_ID_STORAGE_KEY,
  PAYWALL_LOCATION_STORAGE_KEY,
  PAYWALL_TRIGGER_STORAGE_KEY,
  type PaywallTrackingProps,
} from './use-paywall-tracking';

export type CheckoutTrackingProps = {
  tier: string;
  periodicity: string;
  isFreeTrial?: boolean;
};

export type CheckoutFailedProps = CheckoutTrackingProps & {
  error_message?: string;
  error_code?: string;
};

/**
 * Hook for tracking checkout and conversion events.
 * Uses paywall tracking data from sessionStorage to include paywall context.
 */
export const useCheckoutTracking = () => {
  const pico = usePico();
  const isFakeDoor =
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('fdsource');

  const getPaywallContext = () => {
    const urlParams =
      typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;

    const fromStorage = (key: string) => sessionStorage.getItem(key) ?? null;
    const fromUrl = (param: string) => urlParams?.get(param) || null;

    return {
      paywall_id: fromStorage(PAYWALL_ID_STORAGE_KEY),
      paywall_trigger: fromStorage(PAYWALL_TRIGGER_STORAGE_KEY) ?? fromUrl('paywall_trigger'),
      paywall_feature: fromStorage(PAYWALL_FEATURE_STORAGE_KEY) ?? fromUrl('paywall_feature'),
      paywall_location: fromStorage(PAYWALL_LOCATION_STORAGE_KEY) ?? fromUrl('paywall_location'),
    };
  };

  const trackCheckoutOpen = useCallback(
    (props: CheckoutTrackingProps) => {
      if (pico === null || isFakeDoor) {
        return;
      }
      pico.track('checkout_open', {
        tier: props.tier,
        periodicity: props.periodicity,
        is_free_trial: props.isFreeTrial ?? false,
        ...getPaywallContext(),
      });
    },
    [pico],
  );

  const trackCheckoutPurchaseButtonClicked = useCallback(
    (props: CheckoutTrackingProps) => {
      if (pico === null || isFakeDoor) {
        return;
      }
      pico.track('checkout_purchase_button_clicked', {
        tier: props.tier,
        periodicity: props.periodicity,
        is_free_trial: props.isFreeTrial ?? false,
        ...getPaywallContext(),
      });
    },
    [pico],
  );

  const trackCheckoutFailed = useCallback(
    (props: CheckoutFailedProps) => {
      if (pico === null || isFakeDoor) {
        return;
      }
      pico.track('checkout_failed', {
        tier: props.tier,
        periodicity: props.periodicity,
        is_free_trial: props.isFreeTrial ?? false,
        error_message: props.error_message ?? null,
        error_code: props.error_code ?? null,
        ...getPaywallContext(),
      });
    },
    [pico],
  );

  const trackUserConverted = useCallback(
    (props: CheckoutTrackingProps) => {
      if (pico === null || isFakeDoor) {
        return;
      }
      pico.track('user_converted', {
        tier: props.tier,
        periodicity: props.periodicity,
        is_free_trial: props.isFreeTrial ?? false,
        ...getPaywallContext(),
      });
    },
    [pico],
  );

  return {
    trackCheckoutOpen,
    trackCheckoutPurchaseButtonClicked,
    trackCheckoutFailed,
    trackUserConverted,
  };
};

type BuildCheckoutUrlProps = Omit<PaywallTrackingProps, 'paywallType'> & {
  tier: string;
  isTrial?: boolean;
  paywallType?: PaywallTrackingProps['paywallType'];
};

/**
 * Builds a URL to /checkout/{tier} (or /checkout/{tier}/trial) with paywall tracking parameters.
 * These parameters are used to track checkout and conversion attribution.
 */
export const buildCheckoutUrl = (
  checkoutTracking: BuildCheckoutUrlProps,
  additionalParams?: Record<string, string>,
): string => {
  const path = checkoutTracking.isTrial
    ? `/checkout/${checkoutTracking.tier}/trial`
    : `/checkout/${checkoutTracking.tier}`;

  const params = new URLSearchParams({
    paywall_trigger: checkoutTracking.paywallTrigger,
    paywall_location: checkoutTracking.paywallLocation,
    paywall_type: checkoutTracking.paywallType ?? 'popup',
    paywall_feature: checkoutTracking.paywallFeature,
    ...additionalParams,
  });

  return `${path}?${params.toString()}`;
};
